/**
 * OnboardingTour Component
 * Provides guided tour functionality for first-time users
 */
import React, { Component } from "react";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";
import { withRouter } from "react-router-dom";
import { getTourNameByPath, getStepsByTourName } from "./tourSteps";

// LocalStorage keys for tour completion tracking
const TOUR_COMPLETED_PREFIX = "vmass_tour_completed_";
const TOUR_GLOBAL_DISMISSED = "vmass_tour_global_dismissed";

// Custom styles for the tour
const tourStyles = {
  options: {
    primaryColor: "#4f46e5",
    backgroundColor: "#ffffff",
    textColor: "#1e293b",
    overlayColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10000,
    arrowColor: "#ffffff",
  },
  tooltip: {
    borderRadius: 12,
    padding: 20,
  },
  tooltipContainer: {
    textAlign: "left",
  },
  tooltipTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 8,
  },
  tooltipContent: {
    fontSize: 14,
    color: "#64748b",
    lineHeight: 1.6,
  },
  buttonNext: {
    backgroundColor: "#4f46e5",
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
  },
  buttonBack: {
    color: "#64748b",
    marginRight: 10,
  },
  buttonSkip: {
    color: "#94a3b8",
  },
  spotlight: {
    borderRadius: 8,
  },
};

// Vietnamese locale
const locale = {
  back: "Quay lại",
  close: "Đóng",
  last: "Hoàn thành",
  next: "Tiếp theo",
  skip: "Bỏ qua",
  open: "Mở",
};

class OnboardingTour extends Component {
  constructor(props) {
    super(props);
    this.state = {
      run: false,
      steps: [],
      stepIndex: 0,
      tourName: null,
    };
  }

  componentDidMount() {
    // Delay initial check to give wizard time to open first
    setTimeout(() => {
      this.checkAndStartTour();
    }, 2000);

    // Listen for wizard closed event to trigger tour
    window.addEventListener("wizardClosed", this.handleWizardClosed);
  }

  componentWillUnmount() {
    window.removeEventListener("wizardClosed", this.handleWizardClosed);
  }

  handleWizardClosed = () => {
    // Re-check and start tour after wizard closes
    setTimeout(() => {
      this.checkAndStartTour();
    }, 500);
  };

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.checkAndStartTour();
    }
  }

  checkAndStartTour = () => {
    const { pathname } = this.props.location;
    const currentTourName = getTourNameByPath(pathname);

    if (!currentTourName) {
      this.setState({ run: false });
      return;
    }

    // Check if globally dismissed
    const globalDismissed = localStorage.getItem(TOUR_GLOBAL_DISMISSED);
    if (globalDismissed === "true") {
      return;
    }

    // Check if on mobile device - disable tour on mobile
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      return;
    }

    // Check if wizard is still active (first-time user hasn't completed setup)
    // Method 1: Check localStorage
    const loginStatus = localStorage.getItem("login_status");
    if (loginStatus === "is_first_login" || loginStatus === "wizard_pending") {
      return;
    }

    // Method 2: Check if wizard modal is visible in DOM
    const wizardModal = document.querySelector(".wizard.wizard-default");
    const modalOpen = document.querySelector(".modal.show");
    if (wizardModal && modalOpen) {
      // Wizard modal is open, delay tour
      return;
    }

    // Check if this specific tour was completed
    const tourKey = TOUR_COMPLETED_PREFIX + currentTourName;
    const tourCompleted = localStorage.getItem(tourKey);

    if (tourCompleted === "true") {
      return;
    }

    // Get steps for this tour
    const tourSteps = getStepsByTourName(currentTourName);
    if (tourSteps.length > 0) {
      this.setState({
        tourName: currentTourName,
        steps: tourSteps,
        stepIndex: 0,
      });

      // Delay to ensure DOM is ready and wizard is checked again
      setTimeout(() => {
        // Double check wizard is not open before starting
        const wizardCheck = document.querySelector(".wizard.wizard-default");
        const modalCheck = document.querySelector(".modal.show");
        const loginStatusCheck = localStorage.getItem("login_status");

        if (
          (wizardCheck && modalCheck) ||
          loginStatusCheck === "is_first_login" ||
          loginStatusCheck === "wizard_pending"
        ) {
          return;
        }

        this.setState({ run: true });
      }, 2000);
    }
  };

  handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;
    const { tourName } = this.state;

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        this.setState({ stepIndex: index + 1 });
      } else if (action === ACTIONS.PREV) {
        this.setState({ stepIndex: index - 1 });
      }
    }

    // Handle tour completion or skip
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      this.setState({ run: false });

      // Mark this tour as completed
      if (tourName) {
        localStorage.setItem(TOUR_COMPLETED_PREFIX + tourName, "true");
      }
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      this.setState({ run: false });
    }
  };

  render() {
    const { run, steps, stepIndex } = this.state;

    if (!steps.length) {
      return null;
    }

    return (
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        disableScrolling={false}
        scrollToFirstStep
        spotlightClicks
        callback={this.handleJoyrideCallback}
        locale={locale}
        styles={tourStyles}
      />
    );
  }
}

// Export a function to manually trigger tour
export const resetTour = (tourName) => {
  if (tourName) {
    localStorage.removeItem(TOUR_COMPLETED_PREFIX + tourName);
  }
};

// Export a function to reset all tours
export const resetAllTours = () => {
  localStorage.removeItem(TOUR_COMPLETED_PREFIX + "dashboard");
  localStorage.removeItem(TOUR_COMPLETED_PREFIX + "product");
  localStorage.removeItem(TOUR_COMPLETED_PREFIX + "analytics");
  localStorage.removeItem(TOUR_GLOBAL_DISMISSED);
};

// Export a function to check if tour was completed
export const isTourCompleted = (tourName) => {
  return localStorage.getItem(TOUR_COMPLETED_PREFIX + tourName) === "true";
};

export default withRouter(OnboardingTour);
